// "Appears in" — which of its website's NAMED menus (header, footer, …) this
// page is listed in. Saved immediately through /api/site-menus (the menus
// live on the website node, not in the page model, so the page's own Save
// does not carry them). Only shown for a page inside a website.

import { useEffect, useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { getPageMenus, setPageMenus, type PageMenusInfo } from '@/api/siteMenus'

const LABEL: React.CSSProperties = {
  fontSize: '0.8125rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
  color: 'var(--color-text-secondary)', marginBottom: 4, display: 'block',
}
const HELP: React.CSSProperties = { margin: '4px 0 0', fontSize: '0.8125rem', lineHeight: 1.45, color: 'var(--color-text-secondary)' }

function menuTitle(name: string): string {
  return name === 'header' ? 'Header' : name === 'footer' ? 'Footer' : name
}

export default function PageMenusPanel({ project, pageObjName, editUrl, onChanged }: {
  project: string
  pageObjName: string
  /** Website settings (where menus are created and ordered). */
  editUrl?: string
  /** Called after a change so the canvas re-reads the site's menus. */
  onChanged?: () => void
}) {
  const [info, setInfo] = useState<PageMenusInfo | null>(null)
  const [saving, setSaving] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setError(null)
    getPageMenus(project, pageObjName)
      .then((r) => { if (!cancelled) setInfo(r) })
      .catch((err) => {
        console.error('[site-menus] could not load the page menus', err)
        if (!cancelled) setError('Could not load this website’s menus.')
      })
    return () => { cancelled = true }
  }, [project, pageObjName])

  if (error) return <div><span style={LABEL}>Appears in</span><p style={{ ...HELP, color: 'var(--color-error)' }}>{error}</p></div>
  if (!info || !info.site_id) return null

  const toggle = async (name: string, on: boolean) => {
    const wanted = on ? [...info.membership, name] : info.membership.filter((m) => m !== name)
    setSaving(name)
    setError(null)
    try {
      const membership = await setPageMenus(project, pageObjName, wanted)
      setInfo({ ...info, membership })
      onChanged?.()
    } catch (err) {
      console.error('[site-menus] could not save the page menus', err)
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(`Could not save${detail ? `: ${detail}` : '.'}`)
    } finally {
      setSaving(null)
    }
  }

  const editLink = editUrl ? (
    <a href={editUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
      website settings <ExternalLink size={10} />
    </a>
  ) : 'the website settings'

  return (
    <div>
      <span style={LABEL}>Appears in</span>
      {info.menus.length ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px' }}>
          {info.menus.map((name) => (
            <label key={name} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8625rem', color: 'var(--color-text)' }}>
              <input
                type="checkbox"
                checked={info.membership.includes(name)}
                disabled={saving !== null}
                onChange={(e) => void toggle(name, e.target.checked)}
              />
              {menuTitle(name)} menu
            </label>
          ))}
        </div>
      ) : null}
      <p style={HELP}>
        {info.legacy
          ? <>This website still takes its menus from folders in the navigation tree. Convert it in the {editLink} to choose menus here.</>
          : info.menus.length
            ? <>Saved straight away. Order and labels are set in the {editLink}.</>
            : <>{info.site_title || 'This website'} has no menus yet — add a header or footer menu in the {editLink}.</>}
      </p>
    </div>
  )
}
